import { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardContent } from '@auctisync/ui';
import type { Tables } from '@auctisync/shared';
import { askLotQuestion, fetchLotQuestions } from '@auctisync/api-client';
import { supabase } from '../lib/supabase';
import { readClaims, useSession } from '../hooks/useSession';

type LotQuestion = Pick<
  Tables<'lot_questions'>,
  'id' | 'question_text' | 'answer_text' | 'answered_at' | 'created_at'
>;

interface LotQAProps {
  lotId: string;
  /**
   * Tenant id that owns the lot's auction. Used to decide whether the
   * signed-in caller is tenant_staff for THIS lot. If null, the answer UI
   * never appears (read-only mode).
   */
  tenantId: string | null;
}

/**
 * Module #23 chunk 4 — Q&A for a lot.
 *
 * Three modes per caller:
 *   - Anonymous:     read all answered + unanswered questions
 *   - Bidder:        read + ask a new question
 *   - Tenant staff:  read + answer pending questions (inline)
 *
 * Per-defaults: flat (no nesting), tenant_staff moderation only, public
 * visibility once answered (and visible while pending too — bidders can
 * see what's already been asked to avoid duplicate questions).
 */
export function LotQA({ lotId, tenantId }: LotQAProps) {
  const { session } = useSession();
  const claims = readClaims(session);
  const userId = session?.user?.id ?? null;

  // Staff can answer if they're super_admin OR they have a tenant_staff /
  // tenant_owner role for the lot's tenant. The latter we can derive from
  // the JWT claims (set by the custom access token hook) without an extra DB
  // round-trip on every page load.
  const isStaffForLot =
    !!claims.isSuperAdmin ||
    (!!claims.tenantId &&
      tenantId !== null &&
      claims.tenantId === tenantId &&
      (claims.appRole === 'tenant_owner' || claims.appRole === 'tenant_staff'));

  const [questions, setQuestions] = useState<LotQuestion[] | null>(null);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAnswerId, setPendingAnswerId] = useState<string | null>(null);
  const [answerDraft, setAnswerDraft] = useState('');
  const [pendingHideId, setPendingHideId] = useState<string | null>(null);
  const [hideReason, setHideReason] = useState('');

  // Reading and asking go through @auctisync/api-client so the native app runs
  // the identical column list, ordering and validation. They used to be inline
  // here, which is why the app had no Q&A at all — there was nothing to share.
  const load = useCallback(async () => {
    const result = await fetchLotQuestions(supabase, lotId);
    if (!result.ok) {
      setError(result.message);
      setQuestions([]);
      return;
    }
    setQuestions(
      result.questions.map((q) => ({
        id: q.id,
        question_text: q.questionText,
        answer_text: q.answerText,
        answered_at: q.answeredAt,
        created_at: q.createdAt,
      })),
    );
  }, [lotId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleAsk(e: React.FormEvent) {
    e.preventDefault();
    if (!userId || draft.trim().length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await askLotQuestion(supabase, { lotId, userId, draft });
      if (!result.ok) throw new Error(result.message);
      setDraft('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAnswer(questionId: string) {
    if (answerDraft.trim().length === 0 || !userId) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('lot_questions')
        .update({
          answer_text: answerDraft.trim(),
          answered_at: new Date().toISOString(),
          answered_by: userId,
        })
        .eq('id', questionId);
      if (err) throw err;
      setPendingAnswerId(null);
      setAnswerDraft('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleHide(questionId: string) {
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('lot_questions')
        .update({
          hidden_at: new Date().toISOString(),
          hidden_reason: hideReason.trim() || null,
        })
        .eq('id', questionId);
      if (err) throw err;
      setPendingHideId(null);
      setHideReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section style={{ marginTop: '2rem' }}>
      <h2
        style={{
          fontSize: '0.85rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.75rem',
        }}
      >
        Questions & Answers
      </h2>

      {/* Ask form (signed-in bidders only) */}
      {session ? (
        <form
          onSubmit={(e) => {
            void handleAsk(e);
          }}
          style={{ marginBottom: '1rem' }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask the seller a question about this lot…"
            rows={2}
            maxLength={2000}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border)',
              borderRadius: '0.375rem',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" size="sm" disabled={submitting || draft.trim().length === 0}>
              {submitting ? 'Posting…' : 'Ask'}
            </Button>
          </div>
        </form>
      ) : (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          <a href="/my" style={{ color: 'var(--brand-accent)' }}>
            Sign in
          </a>{' '}
          to ask a question about this lot.
        </p>
      )}

      {error ? (
        <p style={{ color: 'var(--text-danger, #991b1b)', fontSize: '0.85rem' }}>{error}</p>
      ) : null}

      {/* List */}
      {questions === null ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</p>
      ) : questions.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          No questions yet — be the first to ask.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {questions.map((q) => (
            <Card key={q.id}>
              <CardContent style={{ padding: '0.85rem 1rem' }}>
                {pendingHideId === q.id ? (
                  <div>
                    <p
                      style={{
                        color: 'var(--text)',
                        marginBottom: '0.5rem',
                        fontSize: '0.9rem',
                      }}
                    >
                      Hide this question from bidders? This cannot be undone from the UI.
                    </p>
                    <p
                      style={{
                        color: 'var(--text-muted)',
                        fontSize: '0.85rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <strong>Q:</strong> {q.question_text}
                    </p>
                    <input
                      type="text"
                      value={hideReason}
                      onChange={(e) => setHideReason(e.target.value)}
                      placeholder="Optional internal reason (e.g. 'spam', 'PII')"
                      maxLength={200}
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.6rem',
                        border: '1px solid var(--border)',
                        borderRadius: '0.375rem',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        fontSize: '0.85rem',
                        fontFamily: 'inherit',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          void handleHide(q.id);
                        }}
                        disabled={submitting}
                      >
                        {submitting ? 'Hiding…' : 'Hide question'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPendingHideId(null);
                          setHideReason('');
                        }}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ color: 'var(--text)', marginBottom: '0.5rem' }}>
                      <strong>Q:</strong> {q.question_text}
                    </p>
                    {q.answer_text ? (
                      <p style={{ color: 'var(--text-muted)' }}>
                        <strong>A:</strong> {q.answer_text}
                      </p>
                    ) : isStaffForLot && pendingAnswerId === q.id ? (
                      <div style={{ marginTop: '0.5rem' }}>
                        <textarea
                          value={answerDraft}
                          onChange={(e) => setAnswerDraft(e.target.value)}
                          placeholder="Type your answer…"
                          rows={2}
                          maxLength={4000}
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.6rem',
                            border: '1px solid var(--border)',
                            borderRadius: '0.375rem',
                            background: 'var(--surface)',
                            color: 'var(--text)',
                            fontSize: '0.85rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              void handleAnswer(q.id);
                            }}
                            disabled={submitting || answerDraft.trim().length === 0}
                          >
                            {submitting ? 'Saving…' : 'Save answer'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPendingAnswerId(null);
                              setAnswerDraft('');
                            }}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : !isStaffForLot ? (
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        Awaiting answer from the seller.
                      </p>
                    ) : null}
                    {isStaffForLot && pendingAnswerId !== q.id ? (
                      <div
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          marginTop: q.answer_text ? '0.5rem' : '0',
                        }}
                      >
                        {!q.answer_text ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPendingAnswerId(q.id);
                              setAnswerDraft('');
                            }}
                          >
                            Answer this
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPendingHideId(q.id);
                            setHideReason('');
                          }}
                        >
                          Hide
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
